import React, { Suspense, lazy } from 'react';
import qs from 'qs';
import { Redirect } from 'react-router';
import { NotFoundError } from 'tradera-components/errors/not-found-error';
import Loader from 'tradera-components/loader/loader';
import StartPage from './pages/start-page'; // Startpage is included for speed
import CartPage from './pages/cart-page';
import { RouteMapRoutes } from 'tradera-components/route-map-routes/route-map-routes';
import { PageMeta } from 'tradera-components/page-meta/page-meta';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { traderaRoutes } from './routes';
import featureToggles from '../my-tradera/app/utils/feature-toggles';

// Lacy load other pages
const BrandPage = lazy(() =>
    import(
        /* webpackChunkName: "brand-page" */ 'tradera-apps/tradera/pages/brand-page'
    )
);
const BrandListPage = lazy(() =>
    import(
        /* webpackChunkName: "brand-list-page" */ 'tradera-apps/tradera/pages/brand-list-page'
    )
);
const CampaignPage = lazy(() =>
    import(
        /* webpackChunkName: "campaign-page" */ 'tradera-apps/tradera/pages/campaign-page'
    )
);
const CategoryListPage = lazy(() =>
    import(
        /* webpackChunkName: "category-list-page" */ 'tradera-apps/tradera/pages/category-list-page'
    )
);
const CategoryPage = lazy(() =>
    import(
        /* webpackChunkName: "category-page" */ 'tradera-apps/tradera/pages/category-page'
    )
);
const CheckoutPage = lazy(() =>
    import(
        /* webpackChunkName: "checkout-page" */ 'tradera-apps/tradera/pages/checkout-page'
    )
);
const CheckoutConfirmationPage = lazy(() =>
    import(
        /* webpackChunkName: "checkout-confirmation-page" */ 'tradera-apps/tradera/pages/checkout-confirmation-page'
    )
);
const FavouriteSellersPage = lazy(() =>
    import(
        /* webpackChunkName: "favourite-sellers" */ 'tradera-apps/tradera/pages/favourite-sellers-page'
    )
);
const Landing_GantXTradera = lazy(() =>
    import(
        /* webpackChunkName: "landing_gantxtradera" */ 'tradera-apps/tradera/pages/landing/gant-x-tradera'
    )
);
const Landing_StadiumXTradera = lazy(() =>
    import(
        /* webpackChunkName: "landing-stadiumxtradera" */ 'tradera-apps/tradera/pages/landing/stadium-x-tradera'
    )
);
const ProfilePage = lazy(() =>
    import(
        /* webpackChunkName: "profile-page" */ 'tradera-apps/tradera/pages/profile-page'
    )
);
const ShippingInfoPage = lazy(() =>
    import(
        /* webpackChunkName: "shippingcalculator" */ 'tradera-apps/tradera/pages/shipping-info-page'
    )
);
const CombinedShippingInfoPage = lazy(() =>
    import(
        /* webpackChunkName: "combined-shipping" */ 'tradera-apps/tradera/pages/combined-shipping-info'
    )
);
const RecommendationsPage = lazy(() =>
    import(
        /* webpackChunkName: "profile-page" */ 'tradera-apps/tradera/pages/recommendations-page'
    )
);
const ReportItemPage = lazy(() =>
    import(
        /* webpackChunkName: "report-item-page" */ 'tradera-apps/tradera/pages/report-item-page'
    )
);
const SearchPage = lazy(() =>
    import(
        /* webpackChunkName: "search-page" */ 'tradera-apps/tradera/pages/search-page'
    )
);
const SwishApp2AppCallbackPage = lazy(() =>
    import(
        /* webpackChunkName: "swish-app2app-callback-page" */ 'tradera-apps/tradera/pages/swish-app2app-callback-page'
    )
);
const TraderaShopPage = lazy(() =>
    import(
        /* webpackChunkName: "tradera-shop" */ 'tradera-apps/tradera/pages/tradera-shop-page'
    )
);
const VerifyPage = lazy(() =>
    import(
        /* webpackChunkName: "verify-page" */ 'tradera-apps/tradera/pages/verify-page'
    )
);
const ViewItemPage = lazy(() =>
    import(
        /* webpackChunkName: "view-item-page" */ 'tradera-apps/tradera/pages/view-item-page'
    )
);

const UPI = lazy(() =>
    import(/* webpackChunkName: "upi" */ 'tradera-apps/tradera/pages/upi')
);

const Dcs = lazy(() =>
    import(/* webpackChunkName: "dis" */ 'tradera-apps/tradera/pages/dcs')
);

const DcsConfirmationMcp = lazy(() =>
    import(
        /* webpackChunkName: "dis" */ 'tradera-apps/tradera/pages/dcs-confirmation-mcp'
    )
);

const INR = lazy(() =>
    import(/* webpackChunkName: "inr" */ 'tradera-apps/tradera/pages/inr')
);

const Cause = lazy(() =>
    import(/* webpackChunkName: "cause" */ 'tradera-apps/tradera/pages/cause')
);

const CausesLandingPage = lazy(() =>
    import(
        /* webpackChunkName: "causes-landing-page" */ 'tradera-apps/tradera/pages/causes-landing-page'
    )
);

const CausesInfo = lazy(() =>
    import(
        /* webpackChunkName: "causes-info" */ 'tradera-apps/tradera/pages/causes-info'
    )
);

const Messaging = lazy(() =>
    import(
        /* webpackChunkName: "causes-info" */ 'tradera-apps/tradera/pages/messaging'
    )
);

const ValuationPage = lazy(() =>
    import(
        /* webpackChunkName: "valuation-page" */ 'tradera-apps/tradera/pages/valuation-page'
    )
);

const PrintPage = lazy(() =>
    import(
        /* webpackChunkName: "print-page" */ 'tradera-apps/tradera/pages/print-page'
    )
);

export const routeMap = [
    {
        ...traderaRoutes.startPage,
        onLoaded: () => {
            GtmService.trackGtmEvent('viewHomePage');
        },
        render: props => <StartPage {...props} />
    },
    {
        ...traderaRoutes.viewItemPage,
        mustLoadDataBeforePageTracking: true,
        render: props => <ViewItemPage {...props} />
    },
    {
        ...traderaRoutes.traderaShopPage,
        render: props => <TraderaShopPage {...props} />
    },
    {
        ...traderaRoutes.causesInfoPage,
        render: props => <CausesInfo {...props} />
    },
    {
        ...traderaRoutes.causesPage,
        mustLoadDataBeforePageTracking: true,
        render: props => <CausesLandingPage {...props} />
    },
    {
        ...traderaRoutes.causePage,
        mustLoadDataBeforePageTracking: true,
        render: props => <Cause {...props} />
    },
    {
        ...traderaRoutes.upiPage,
        render: props =>
            featureToggles.isDiscontinuedSaleEnabled ? (
                <Dcs {...props} />
            ) : (
                <UPI {...props} />
            )
    },
    {
        ...traderaRoutes.discontiunedSalePage,
        render: props => <Dcs {...props} />
    },
    {
        ...traderaRoutes.discontiunedSaleConfirmationPage,
        render: props => <DcsConfirmationMcp {...props} />
    },
    {
        ...traderaRoutes.inrPage,
        render: props => <INR {...props} />
    },
    {
        ...traderaRoutes.brandPage,
        render: props => {
            const brand = props.match.params.brand;
            return <BrandPage brand={brand} />;
        }
    },
    {
        ...traderaRoutes.brandListPage,
        render: props => <BrandListPage {...props} />
    },
    {
        ...traderaRoutes.campaignPage,
        mustLoadDataBeforePageTracking: true,
        render: props => <CampaignPage {...props} />
    },
    {
        ...traderaRoutes.categoryListPage,
        render: props => <CategoryListPage {...props} />
    },
    {
        ...traderaRoutes.checkoutConfirmationPage,
        render: props => <CheckoutConfirmationPage {...props} />
    },
    {
        ...traderaRoutes.checkoutPage,
        render: props => <CheckoutPage {...props} />
    },
    {
        ...traderaRoutes.cartPage,
        render: props => <CartPage {...props} />
    },
    {
        ...traderaRoutes.searchResultPage,
        mustLoadDataBeforePageTracking: true,
        onLoaded: getState => {
            const { itemsById, queryParams } = getState().discover;
            GtmService.trackGtmEvent('search', {
                searchTerm: queryParams.q,
                itemsById
            });
        },
        render: props => <SearchPage {...props} />
    },
    {
        ...traderaRoutes.favouriteSellersPage,
        render: props => <FavouriteSellersPage {...props} />
    },
    {
        ...traderaRoutes.landing_GantXTradera,
        render: props => <Landing_GantXTradera {...props} />
    },
    {
        ...traderaRoutes.landing_StadiumXTradera,
        render: props => <Landing_StadiumXTradera {...props} />
    },
    {
        ...traderaRoutes.profilePage,
        mustLoadDataBeforePageTracking: true,
        render: props => <ProfilePage {...props} />
    },
    {
        ...traderaRoutes.recommendationsPage,
        render: props => <RecommendationsPage {...props} />
    },
    {
        ...traderaRoutes.reportItemPage,
        render: props => <ReportItemPage {...props} />
    },
    {
        ...traderaRoutes.swishApp2AppCallbackPage,
        render: props => (
            <SwishApp2AppCallbackPage
                swishResult={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).swishResult
                }
                {...props}
            />
        )
    },
    {
        ...traderaRoutes.verifyPage,
        render: props => (
            <VerifyPage
                // TODO: Move into VerifyPage component
                returnUrl={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).returnUrl
                }
                {...props}
            />
        )
    },
    {
        ...traderaRoutes.CombinedShippingInfoPage,
        render: props => <CombinedShippingInfoPage {...props} />
    },
    {
        ...traderaRoutes.ShippingInfoPage,
        render: props => <ShippingInfoPage {...props} />
    },
    {
        ...traderaRoutes.ValuationPage,
        render: props => <ValuationPage {...props} />
    },
    {
        ...traderaRoutes.PrintPage,
        render: props => <PrintPage {...props} />
    },
    {
        ...traderaRoutes.categoryResultPage,
        mustLoadDataBeforePageTracking: true,
        render: props => <CategoryPage {...props} />
    },
    {
        ...traderaRoutes.messagingiframe,
        render: props => <Messaging {...props} />
    },
    {
        ...traderaRoutes.legacyCategoryResultPage,
        render: props => {
            const s = props.match.params.categoryNameAndId;
            const divider = s.indexOf('_') > -1 ? '_' : '-';
            const parts = s.split(divider);
            const categoryId = parts.pop();
            return (
                <Redirect
                    to={`/category/${categoryId}${props.location.search}`}
                />
            );
        }
    },
    {
        ...traderaRoutes.notFoundPage,
        render: () => <NotFoundError />
    }
];

const SuspenseFallback = () => {
    window.scrollTo(0, 0);
    return <Loader usePortal={false} />;
};

export const Routes = () => {
    return (
        <Suspense fallback={<SuspenseFallback />}>
            <PageMeta routeMap={routeMap} />
            <RouteMapRoutes routeMap={routeMap} />
        </Suspense>
    );
};
